import { cond, pipe } from 'ramda';
import { get, snakeCase } from 'lodash-es';
import { ULYTICS_ACTIONS } from '@updater/ulytics';
import {
  track,
  RECEIVE,
  VIEW,
  CHANGE,
  CLICK,
  SELECT,
  CLOSE,
  SUBMIT,
  DISPLAY,
  TRACK,
  UPDATE,
  MEASURE,
  BOUNCE,
  ERROR,
  TRY,
  VERIFY,
  CREATE,
  SAVE,
  CONFIRM,
  CANCEL,
  DISMISS,
  ENTER,
  LOAD,
  VALIDATE,
  SENT,
  RESENT,
  ADD,
  REMOVE,
  OPEN,
  ENGAGE,
} from '@updater-core/lib/tracker';
import { analytics, router } from '../selectors';

const isTrackAction = ({ type }) => type === ULYTICS_ACTIONS.TRACK;
const isNotTrack = (action) => !isTrackAction(action);
const isPageAction = ({ type }) => type === ULYTICS_ACTIONS.PAGE;

const eventNameHasText = (verb) => (action) => {
  const object = get(action, 'payload.event');
  return object ? object.toLowerCase().includes(verb) : undefined;
};

const withoutString = (stringToRemove) => (str) =>
  str.toLowerCase().replace(stringToRemove, '').trim();
const objectSelector = (strs) => (action) => {
  const object = get(action, 'payload.event');
  return object
    ? pipe(...strs.map(withoutString), snakeCase)(object)
    : undefined;
};

const domainSelector = (domains) => (action) => {
  const event = get(action, 'payload.event');
  return event
    ? domains.filter((domain) => event.toLowerCase().includes(domain))[0] ||
        domains[0]
    : domains[0];
};

// prettier-ignore
const extractionSwitch = [
  [isPageAction,                                    domainSelector(['mover', 'moveiq'])  ,  objectSelector(['moveiq'])                         ,  () => VIEW        ],
  [isNotTrack,                                      () => undefined                      ,  () => undefined                                    ,  () => undefined   ],
  [eventNameHasText('received'),                    domainSelector(['mover', 'moveiq'])  ,  objectSelector(['received' , 'moveiq'])            ,  () => RECEIVE     ],
  [eventNameHasText('viewed'),                      domainSelector(['mover', 'moveiq'])  ,  objectSelector(['viewed'   , 'moveiq'])            ,  () => VIEW        ],
  [eventNameHasText('changed'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['changed'  , 'moveiq'])            ,  () => CHANGE      ],
  [eventNameHasText('clicked'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['clicked'  , 'moveiq'])            ,  () => CLICK       ],
  [eventNameHasText('selected'),                    domainSelector(['mover', 'moveiq'])  ,  objectSelector(['selected' , 'moveiq'])            ,  () => SELECT      ],
  [eventNameHasText('closed'),                      domainSelector(['mover', 'moveiq'])  ,  objectSelector(['closed'   , 'moveiq'])            ,  () => CLOSE       ],
  [eventNameHasText('submitted'),                   domainSelector(['mover', 'moveiq'])  ,  objectSelector(['submitted', 'moveiq'])            ,  () => SUBMIT      ],
  [eventNameHasText('displayed'),                   domainSelector(['mover', 'moveiq'])  ,  objectSelector(['displayed', 'moveiq'])            ,  () => DISPLAY     ],
  [eventNameHasText('tracked'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['tracked'  , 'moveiq'])            ,  () => TRACK       ],
  [eventNameHasText('updated'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['updated'  , 'moveiq'])            ,  () => UPDATE      ],
  [eventNameHasText('measured'),                    domainSelector(['mover', 'moveiq'])  ,  objectSelector(['measured' , 'moveiq'])            ,  () => MEASURE     ],
  [eventNameHasText('bounced'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['bounced'  , 'moveiq'])            ,  () => BOUNCE      ],
  [eventNameHasText('error'),                       domainSelector(['mover', 'moveiq'])  ,  objectSelector(['error'    , 'moveiq'])            ,  () => ERROR       ],
  [eventNameHasText('tried'),                       domainSelector(['mover', 'moveiq'])  ,  objectSelector(['tried'    , 'moveiq'])            ,  () => TRY         ],
  [eventNameHasText('verified'),                    domainSelector(['mover', 'moveiq'])  ,  objectSelector(['verified' , 'moveiq'])            ,  () => VERIFY      ],
  [eventNameHasText('created'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['created'  , 'moveiq'])            ,  () => CREATE      ],
  [eventNameHasText('saved'),                       domainSelector(['mover', 'moveiq'])  ,  objectSelector(['saved'    , 'moveiq'])            ,  () => SAVE        ],
  [eventNameHasText('confirmed'),                   domainSelector(['mover', 'moveiq'])  ,  objectSelector(['confirmed', 'moveiq'])            ,  () => CONFIRM     ],
  [eventNameHasText('confirm'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['confirm'  , 'moveiq'])            ,  () => CONFIRM     ],
  [eventNameHasText('canceled'),                    domainSelector(['mover', 'moveiq'])  ,  objectSelector(['canceled' , 'moveiq'])            ,  () => CANCEL      ],
  [eventNameHasText('cancelled'),                   domainSelector(['mover', 'moveiq'])  ,  objectSelector(['cancelled', 'moveiq'])            ,  () => CANCEL      ],
  [eventNameHasText('dismissed'),                   domainSelector(['mover', 'moveiq'])  ,  objectSelector(['dismissed', 'moveiq'])            ,  () => DISMISS     ],
  [eventNameHasText('entered'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['entered'  , 'moveiq'])            ,  () => ENTER       ],
  [eventNameHasText('loaded'),                      domainSelector(['mover', 'moveiq'])  ,  objectSelector(['loaded'   , 'moveiq'])            ,  () => LOAD        ],
  [eventNameHasText('validated'),                   domainSelector(['mover', 'moveiq'])  ,  objectSelector(['validated', 'moveiq'])            ,  () => VALIDATE    ],
  [eventNameHasText('sent'),                        domainSelector(['mover', 'moveiq'])  ,  objectSelector(['sent'     , 'moveiq'])            ,  () => SENT        ],
  [eventNameHasText('resent'),                      domainSelector(['mover', 'moveiq'])  ,  objectSelector(['resent'   , 'moveiq'])            ,  () => RESENT      ],
  [eventNameHasText('added'),                       domainSelector(['mover', 'moveiq'])  ,  objectSelector(['added'    , 'moveiq'])            ,  () => ADD         ],
  [eventNameHasText('removed'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['removed'  , 'moveiq'])            ,  () => REMOVE      ],
  [eventNameHasText('opened'),                      domainSelector(['mover', 'moveiq'])  ,  objectSelector(['opened'   , 'moveiq'])            ,  () => OPEN        ],
  [eventNameHasText('engaged'),                     domainSelector(['mover', 'moveiq'])  ,  objectSelector(['engaged'  , 'moveiq'])            ,  () => ENGAGE        ],  
  [eventNameHasText('signed in'),                   () => 'mover'                        ,  () => 'signin'                                     ,  () => TRACK       ],
  [eventNameHasText('signed out'),                  () => 'mover'                        ,  () => 'signout'                                    ,  () => TRACK       ],
  [eventNameHasText('signed up'),                   () => 'mover'                        ,  () => 'signup'                                     ,  () => TRACK       ],
  [eventNameHasText('set email and password'),      () => 'mover'                        ,  () => 'email_and_password'                         ,  () => SUBMIT      ],
  [eventNameHasText('new move cta'),                () => 'mover'                        ,  () => 'new_move'                                   ,  () => SUBMIT      ],
  [eventNameHasText('link event'),                  () => 'mover'                        ,  () => 'link'                                       ,  () => TRACK       ],
];

export const getDomain = cond(extractionSwitch.map((c) => [c[0], c[1]]));
export const getObject = cond(extractionSwitch.map((c) => [c[0], c[2]]));
export const getVerb = cond(extractionSwitch.map((c) => [c[0], c[3]]));

export function EventTrackerEpic() {
  return (action, { getState }) => {
    const verb = getVerb(action);
    const object = getObject(action);
    const domain = getDomain(action);

    if (verb && object) {
      track({
        segmentEventName: get(action, 'payload.event'),
        domain,
        object,
        verb,
        page: {
          name: router.currentAnalyticsPage(getState()),
        },
        details: {
          ...analytics.staticProperties(getState()),
          ...get(action, 'payload.data'),
        },
        legacyEvent: true,
      });
    }
  };
}
